import type { ReactNode } from 'react';

interface SectionHeaderProps {
  // Uppercase title — the section's identity. Kept terse on purpose.
  title: string;
  // Optional right-aligned count badge (e.g. "4 pending"). Hidden when undefined.
  count?: number;
  countLabel?: string; // e.g. 'pending' → "4 pending"
  // Optional subtitle line under the title (e.g. the selected date).
  subtitle?: string;
  // Optional trailing slot for action affordances (rarely used; kept flexible).
  trailing?: ReactNode;
}

// Shared section header for the Route Tracker right panel. The two sections
// (Needs Attention, Status Updates) use this so visual vocabulary stays identical
// — uppercase title with tight tracking, right-aligned context, hairline separator.
export function SectionHeader({
  title,
  count,
  countLabel,
  subtitle,
  trailing
}: SectionHeaderProps) {
  return (
    <div className='mb-3 flex items-end justify-between gap-3 border-b border-gray-200 pb-2 dark:border-gray-700'>
      <div className='min-w-0'>
        <h2
          className='text-sm font-bold uppercase text-gray-800 dark:text-gray-200'
          style={{ letterSpacing: '0.06em' }}
        >
          {title}
        </h2>
        {subtitle && <p className='mt-0.5 text-xs text-gray-500 dark:text-gray-400'>{subtitle}</p>}
      </div>
      <div className='flex shrink-0 items-center gap-3 text-xs text-gray-500 dark:text-gray-400'>
        {typeof count === 'number' && (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {count} {countLabel ?? ''}
          </span>
        )}
        {trailing}
      </div>
    </div>
  );
}
